import {
    convertDateArrayFromDate,
    convertDateFromDateArray,
} from '@/utils/time';
import { Money } from './money';
import { BackendType, FrontendType, ObjectType, StarjackDate } from './general';
import { isSameDay, parse } from 'date-fns';

export const PC_DEFAULT_DATE_START = parse(
    '2000-01-01',
    'yyyy-MM-dd',
    new Date()
);
export const PC_DEFAULT_DATE_END = parse(
    '2100-01-01',
    'yyyy-MM-dd',
    new Date()
);

// models

export type IntegrationConfiguration = {
    barcode: boolean;
    internalProduction: boolean;
    keycard: boolean;
    pickup: boolean;
};

export enum ProductEnvironment {
    PORTAL = 'PORTAL',
    INTEGRATION = 'INTEGRATION',
    B2B = 'B2B',
    MOBILE = 'MOBILE',
}

export const ALL_PRODUCT_ENVIRONMENTS = [
    ProductEnvironment.PORTAL,
    ProductEnvironment.INTEGRATION,
    ProductEnvironment.B2B,
    ProductEnvironment.MOBILE,
];

export enum ProductType {
    SEASON = 'SEASON',
    DAY = 'DAY',
}

export type ProductName = {
    de: string;
    en: string;
};

export type Product = {
    duration: number;
    name: ProductName;
    type: ProductType;
};
export type BaseCondition<T extends ObjectType = FrontendType> = {
    id: string;
    productConfigurationId: string;
    name: string;
    externalName: {
        de: string;
        en: string;
    };
    sort: number;
    minDaysBeforeTicketStart?: number | string;
    validFrom?: StarjackDate<T>;
    validTo?: StarjackDate<T>;
    startDateValidFrom?: StarjackDate<T>;
    startDateValidTo?: StarjackDate<T>;
    validWeekdays?: T extends FrontendType ? Set<string> : string[];
    startDateValidWeekdays?: T extends FrontendType ? Set<string> : string[];
    personTypeIds: string[];
    sellingGroup?: string;
    notSellingGroup?: string;
    environments: ProductEnvironment[];
    contingentRangeFrom?: StarjackDate<T>;
    contingentRangeTo?: StarjackDate<T>;
};

export const convertBaseConditionToFrontend = (
    bC: BaseCondition<BackendType>
): BaseCondition => ({
    ...bC,
    validWeekdays: new Set(bC.validWeekdays),
    startDateValidWeekdays: new Set(bC.startDateValidWeekdays),
    validFrom: convertDateFromDateArray(bC.validFrom),
    validTo: convertDateFromDateArray(bC.validTo),
    startDateValidFrom: convertDateFromDateArray(bC.startDateValidFrom),
    startDateValidTo: convertDateFromDateArray(bC.startDateValidTo),
    contingentRangeFrom: convertDateFromDateArray(bC.contingentRangeFrom),
    contingentRangeTo: convertDateFromDateArray(bC.contingentRangeTo),
});

export const convertBaseConditionToBackend = (
    bC: BaseCondition
): BaseCondition<BackendType> => ({
    ...bC,
    validWeekdays: Array.from(bC.validWeekdays.values()),
    startDateValidWeekdays: Array.from(bC.startDateValidWeekdays.values()),
    validFrom: convertDateArrayFromDate(bC.validFrom),
    validTo: convertDateArrayFromDate(bC.validTo),
    startDateValidFrom: convertDateArrayFromDate(bC.startDateValidFrom),
    startDateValidTo: convertDateArrayFromDate(bC.startDateValidTo),
    contingentRangeFrom: convertDateArrayFromDate(bC.contingentRangeFrom),
    contingentRangeTo: convertDateArrayFromDate(bC.contingentRangeTo),
});

const convertPriceConditionToBackend = (
    pC: PriceCondition
): PriceCondition<BackendType> => ({
    ...(convertBaseConditionToBackend(pC) as PriceCondition<BackendType>),
    priceChange: {
        ...pC.priceChange,
        amount: pC.priceChange?.amount * -1,
    },
});

const convertPriceConditionToFrontend = (
    pC: PriceCondition<BackendType>
): PriceCondition => ({
    ...(convertBaseConditionToFrontend(pC) as PriceCondition),
    priceChange: {
        ...pC.priceChange,
        amount: pC.priceChange.amount * -1,
    },
});

export type ContingentCondition<T extends ObjectType = FrontendType> =
    BaseCondition<T> & {
        maxTicketsPerStartDay: number;
        maxTicketsForDateRange?: number;
    };

export type PriceCondition<T extends ObjectType = FrontendType> =
    BaseCondition<T> & {
        priceChange: Money;
        priceChangeAbsolute: boolean;
        blockCancellation: boolean;
        dayContingentFrom: number;
        dayContingentTo: number;
        startDateContingentFrom: number;
        startDateContingentTo: number;
        conditionContingentFrom: number;
        conditionContingentTo: number;
        validityPeriodContingentFrom: number;
        validityPeriodContingentTo: number;
        roundTo: number;
        round50: boolean;
    };

export type ProductConfiguration = {
    enabled: boolean;
    id: string;
    disableStartDateToday: boolean;
    keycardCodes: string[];
    liftOperatorId: string;
    integrationConfiguration: IntegrationConfiguration;
    mobileConfiguration: IntegrationConfiguration;
    portalConfiguration: IntegrationConfiguration;
    b2bConfiguration: IntegrationConfiguration;
    contingentConditions: ContingentCondition[];
    priceConditions: PriceCondition[];
    product: Product;
    productId: string;
    infoTextDE: string;
    infoTextEN: string;
    filterTagsDE: string;
    filterTagsEN: string;
    sortKey: number;
    maxTicketsPerStartDay: number;
    contingentsThreshold: number;
    tags: string[];
    validityRange: {
        from: StarjackDate;
        to: StarjackDate;
    };
    startDateValidityRange: {
        from: StarjackDate;
        to: StarjackDate;
    };
    durationOverride: number;
    productTypeOverride: ProductType;
};

interface MultiString {
    [key: string]: string;
}

export type BackendProductConfiguration = {
    enabled: boolean;
    id: string;
    disableStartDateToday: boolean;
    integrationConfiguration: IntegrationConfiguration;
    keycardCodes: string[];
    liftOperatorId: string;
    contingentConditions: ContingentCondition<BackendType>[];
    priceConditions: PriceCondition<BackendType>[];
    mobileConfiguration: IntegrationConfiguration;
    portalConfiguration: IntegrationConfiguration;
    b2bConfiguration: IntegrationConfiguration;
    product: Product;
    productId: string;
    sortKey: number;
    maxTicketsPerStartDay: number;
    contingentsThreshold: number;
    tags: string[];
    infoText: MultiString;
    filterTags: MultiString;
    validityRange: {
        from: number[];
        to: number[];
    };
    startDateValidityRange: {
        from: number[];
        to: number[];
    };
    durationOverride: number;
    productTypeOverride: ProductType;
};

export const transformProductConfigurationFromResponse = (
    bPc: BackendProductConfiguration
): ProductConfiguration => {
    let startDateValidityFrom = convertDateFromDateArray(
        bPc.startDateValidityRange.from
    );
    let startDateValidityTo = convertDateFromDateArray(
        bPc.startDateValidityRange.to
    );

    if (isSameDay(startDateValidityFrom, PC_DEFAULT_DATE_START)) {
        startDateValidityFrom = null;
    }

    if (isSameDay(startDateValidityFrom, PC_DEFAULT_DATE_START)) {
        startDateValidityTo = null;
    }

    return {
        ...bPc,
        infoTextDE: bPc.infoText?.de,
        infoTextEN: bPc.infoText?.en,
        filterTagsDE: bPc.filterTags?.de,
        filterTagsEN: bPc.filterTags?.en,
        contingentConditions: bPc?.contingentConditions?.map(
            convertBaseConditionToFrontend
        ) as ContingentCondition[],
        priceConditions: bPc?.priceConditions?.map(
            convertPriceConditionToFrontend
        ),
        validityRange: {
            from: convertDateFromDateArray(bPc.validityRange.from),
            to: convertDateFromDateArray(bPc.validityRange.to),
        },
        startDateValidityRange: {
            from: startDateValidityFrom,
            to: startDateValidityTo,
        },
    };
};

export const transformProductConfigurationToBackend = (
    p: ProductConfiguration
): BackendProductConfiguration => {
    // PC_DEFAULT_DATE_START
    // PC_DEFAULT_DATE_END

    const configuration = {
        ...p,
        infoText: {
            de: p.infoTextDE,
            en: p.infoTextEN,
        },
        filterTags: {
            de: p.filterTagsDE,
            en: p.filterTagsEN,
        },
        productTypeOverride: p.productTypeOverride || null,
        contingentConditions: p?.contingentConditions?.map(
            convertBaseConditionToBackend
        ) as ContingentCondition<BackendType>[],
        priceConditions: p?.priceConditions?.map(
            convertPriceConditionToBackend
        ),
        validityRange: {
            from: convertDateArrayFromDate(p.validityRange.from),
            to: convertDateArrayFromDate(p.validityRange.to),
        },

        startDateValidityRange:
            p.startDateValidityRange.from !== null
                ? {
                      from: convertDateArrayFromDate(
                          p.startDateValidityRange.from || PC_DEFAULT_DATE_START
                      ),
                      to: convertDateArrayFromDate(
                          p.startDateValidityRange.to || PC_DEFAULT_DATE_END
                      ),
                  }
                : null,
    };
    delete configuration.infoTextDE;
    delete configuration.infoTextEN;
    delete configuration.filterTagsDE;
    delete configuration.filterTagsEN;

    return configuration;
};
